import { useEffect, useMemo, useState } from "react";
import { useChatStore } from "../stores/ChatStore";
import debounce from "lodash.debounce";
import { ChatRoom, ListChatRoom } from "../interfaces/Chat/Chat";

export const useChatSearch = (search: string, selectedFilterProgress: string[], selectedFilterLabel: string, selectedFilterStatus: string, selectedFilterStatusSession: string[], selectedFilterPicMerchant: string[], selectedFilterPicPipeline: string[]) => {
    const { listChatRooms, listChatMessages, listChatAgents, messageNotifications } = useChatStore();

    const [searchResults, setSearchResults] = useState<ListChatRoom>({});
    const [filteredResults, setFilteredResults] = useState<ListChatRoom>({});

    const chatRoomsArray = useMemo(() => Object.values(listChatRooms), [listChatRooms]);
    const chatMessagesArray = useMemo(() => Object.entries(listChatMessages), [listChatMessages]);

    const checkStatusSession = (chat: ChatRoom) => {
        const key = `${chat.roomPhone}-${chat.as}`;
        const chatAgents = listChatAgents[key] || [];
        const lastAgentState = chatAgents[chatAgents.length - 1]?.state;

        // Loop semua filter yang dipilih
        return selectedFilterStatusSession.some(filter => {
            if (filter === "unassigned") {
                // unassigned = tidak ada agent aktif / waiting
                return !(lastAgentState === "active" || lastAgentState === "waiting");
            }
            // filter biasa: cek apakah state terakhir sesuai
            return lastAgentState === filter;
        });
    };

    /*** Filter Chat Berdasarkan Filter ***/
    const applyFilters = (chat: ChatRoom) => {
        const matchesProgress = selectedFilterProgress.length === 0 || chat.pipelines?.some(pipeline =>
            pipeline?.progress?.name && selectedFilterProgress.includes(pipeline.progress.name)
        );

        const matchesPicMerchant = selectedFilterPicMerchant.length === 0 || chat.merchants?.some(merchant =>
            merchant?.PIC?.name && selectedFilterPicMerchant.includes(merchant.PIC.name)
        );

        const matchesPicPipeline = selectedFilterPicPipeline.length === 0 || chat.pipelines?.some(pipeline =>
            pipeline?.PIC?.name && selectedFilterPicPipeline.includes(pipeline.PIC.name)
        );

        const matchesStatusSession = selectedFilterStatusSession.length === 0 || checkStatusSession(chat);

        const matchesLabel = !selectedFilterLabel || chat.labels?.some(label => label.id === selectedFilterLabel);

        const isUnread = messageNotifications[`${chat.roomPhone}-${chat.as}`];
        const matchesStatus = !selectedFilterStatus || (selectedFilterStatus === "unread" && isUnread && (isUnread.unreadCount === null || isUnread.unreadCount > 0)) || (selectedFilterStatus === "read" && !isUnread);

        return matchesProgress && matchesPicMerchant && matchesPicPipeline && matchesLabel && matchesStatusSession && matchesStatus;
    };

    /*** Proses Search ***/
    const searchChats = useMemo(() => {
        return debounce((searchText: string) => {
            if (!searchText) {
                setSearchResults({});
                return;
            }

            let searchMessage = searchText.toLowerCase();
            let searchContact = searchText.toLowerCase();

            if (searchContact.length >= 2 && searchContact.startsWith("08")) {
                searchContact = "628" + searchContact.substring(2);
            }

            const tempResults: ListChatRoom = {};

            // Search berdasarkan nama dan nomor telepon
            chatRoomsArray.forEach((chat: ChatRoom) => {
                if (chat.roomName.toLowerCase().includes(searchContact) || chat.roomPhone.toLowerCase().includes(searchContact) || chat.merchants.some(merchant => merchant.name.toLowerCase().includes(searchContact))) {
                    const key = `${chat.roomPhone}-${chat.as}-contact`;
                    tempResults[key] = { ...chat };
                }
            });

            // Search berdasarkan isi pesan (mencari semua pesan yang cocok)
            chatMessagesArray.forEach(([key, messages]) => {
                const room = listChatRooms[key];

                messages.forEach((msg, index) => {
                    if (msg.message.toLowerCase().includes(searchMessage)) {
                        if (room) {
                            const uniqueKey = `${key}-message-${msg.id}`; // Membuat key unik berdasarkan index pesan
                            tempResults[uniqueKey] = {
                                ...room,
                                chat: msg, // Hanya menyimpan pesan yang cocok
                            };
                        }
                    }
                });
            });

            setSearchResults(tempResults);
        }, 300);
    }, [chatRoomsArray, chatMessagesArray]);

    /*** Proses Filter ***/
    useEffect(() => {
        let dataToFilter = search ? searchResults : listChatRooms;

        const filtered = Object.keys(dataToFilter).reduce((acc: ListChatRoom, key) => {
            const chat = dataToFilter[key];
            if (applyFilters(chat)) {
                if (search) acc[key] = chat;
                else acc[key + '-contact'] = chat;
            }
            return acc;
        }, {});

        setFilteredResults(filtered);
    }, [searchResults, search, selectedFilterProgress, selectedFilterLabel, selectedFilterStatus, selectedFilterStatusSession, selectedFilterPicMerchant, selectedFilterPicPipeline]);

    /*** Jalankan Search Saat Search Berubah ***/
    useEffect(() => {
        searchChats(search);
        return () => searchChats.cancel();
    }, [search, searchChats]);

    return filteredResults;
};
